"use client";

import {JSX} from "react";

import SmartImage from "@/shared/ui/smart-image";

import {useSnapshot} from "valtio";

import {useScrollIntoViewDeferred, type UseScrollIntoViewDeferredOptions} from "@/shared/hooks/dom/use-scroll-into-view-deferred";
import {useTranslation} from "@/shared/i18n";
import type {TranslationKey} from "@/shared/i18n/types";
import type {FormSubmitFeedbackModelInstance} from "@/shared/models/form/form-submit-feedback";
import {Typography} from "@/shared/ui/data-display/typography";
import {assetPath} from "@/shared/utils/asset-path";

import "./styles/form-submit-feedback-success.scss";

const SUCCESS_SCROLL_INTO_VIEW: UseScrollIntoViewDeferredOptions = {
    behavior: "smooth",
    block: "nearest",
    inline: "nearest",
};

export type FormSubmitFeedbackSuccessIcon = "bookingConfirmed";

const SUCCESS_ICON_SRC: Record<FormSubmitFeedbackSuccessIcon, string> = {
    bookingConfirmed: assetPath("/images/icons/booking-confirmed.svg"),
};

export type FormSubmitFeedbackSuccessProps = {
    model: FormSubmitFeedbackModelInstance;
    /** Defaults to `bookingConfirmed`. */
    icon?: FormSubmitFeedbackSuccessIcon;
    iconAlt?: string;
    iconWidth?: number;
    iconHeight?: number;
    className?: string;
};

export function FormSubmitFeedbackSuccess({
    model,
    icon = "bookingConfirmed",
    iconAlt = "",
    iconWidth = 80,
    iconHeight = 80,
    className = "",
}: FormSubmitFeedbackSuccessProps): JSX.Element | null {
    const {t} = useTranslation();
    const snapshot = useSnapshot(model);
    const ref = useScrollIntoViewDeferred<HTMLDivElement>(model, SUCCESS_SCROLL_INTO_VIEW);

    if (!snapshot.isSuccess) return null;

    const b = snapshot.banner;
    if (b.type !== "success") return null;

    const titleKey = b.title?.trim();
    const messageKey = b.message?.trim();
    const displayTitle = titleKey ? t(titleKey as TranslationKey) : "";
    const displayMessage = messageKey ? t(messageKey as TranslationKey) : "";

    const iconSrc = SUCCESS_ICON_SRC[icon];

    return (
        <div ref={ref} className={`form-submit-feedback-success ${className}`.trim()}>
            <SmartImage src={iconSrc} alt={iconAlt} width={iconWidth} height={iconHeight} className="form-submit-feedback-success__icon" />
            {displayTitle.trim().length > 0 && (
                <Typography as="p" size={18} weight={600}>
                    {displayTitle}
                </Typography>
            )}
            {displayMessage.trim().length > 0 && (
                <Typography as="p" size={14} color="paragraph2">
                    {displayMessage}
                </Typography>
            )}
        </div>
    );
}
